import { authHeaders, throwOnError } from "./authHeaders";

/**
 * Client for the patrol report APIs:
 *   /api/reports/officer-performance
 *   /api/reports/location-compliance
 *   /api/reports/missed-patrols
 *
 * All three report the RECORD — scans recorded, alerts raised. None of them
 * assign work, rank people, or order checkpoints into a route, and each
 * response carries an `advisory` field saying so. UI built on these must keep
 * that framing (see api/compliance/status.ts for the canonical statement).
 */

export interface ReportDateRange {
  start: string;
  end: string;
  timezone: string;
  days?: number;
}

// ── Officer performance ──────────────────────────────────────────────────────

export interface OfficerPerformanceRow {
  officer_id: string;
  n_number: string;
  name: string;
  status: string;
  hospital_id: string;
  hospital_name: string | null;
  shifts: number;
  shift_minutes: number;
  scans: number;
  distinct_checkpoints: number;
  active_days: number;
  last_scan_at: string | null;
  /** Null when under 30 minutes of recorded shift time. */
  scans_per_hour: number | null;
}

export interface OfficerPerformanceResponse {
  date_range: ReportDateRange | null;
  advisory: string;
  totals: {
    scans: number;
    attributed_scans: number;
    unattributed_scans: number;
    officers: number;
  };
  officers: OfficerPerformanceRow[];
}

export async function fetchOfficerPerformance(params: {
  hospitalId?: string;
  start?: string;
  end?: string;
  status?: string;
} = {}): Promise<OfficerPerformanceResponse> {
  const q = new URLSearchParams();
  if (params.hospitalId) q.set("hospital_id", params.hospitalId);
  if (params.start) q.set("start", params.start);
  if (params.end) q.set("end", params.end);
  if (params.status) q.set("status", params.status);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const response = await fetch(`/api/reports/officer-performance${suffix}`, {
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to load officer performance");
  return response.json();
}

// ── Location compliance (coverage) ───────────────────────────────────────────

export interface LocationCoverageRow {
  location_id: string;
  hospital_id: string;
  hospital_name: string | null;
  name: string;
  place: string;
  interval_minutes: number;
  min_scans_per_24h: number;
  rule_source: "LOCATION" | "HOSPITAL" | "SYSTEM";
  scans: number;
  days_covered: number;
  days_in_window: number;
  coverage_rate: number;
  expected_scans: number;
  attainment: number;
  last_scan_in_window: string | null;
  last_scanned_at: string | null;
  status: "OK" | "DUE_SOON" | "OVERDUE" | "NEVER_SCANNED";
}

export interface LocationComplianceResponse {
  date_range: ReportDateRange | null;
  advisory: string;
  summary: {
    checkpoints: number;
    fully_covered: number;
    partially_covered: number;
    uncovered: number;
  };
  checkpoints: LocationCoverageRow[];
}

export async function fetchLocationCompliance(params: {
  hospitalId?: string;
  start?: string;
  end?: string;
  sort?: "coverage" | "scans" | "name";
} = {}): Promise<LocationComplianceResponse> {
  const q = new URLSearchParams();
  if (params.hospitalId) q.set("hospital_id", params.hospitalId);
  if (params.start) q.set("start", params.start);
  if (params.end) q.set("end", params.end);
  if (params.sort) q.set("sort", params.sort);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const response = await fetch(`/api/reports/location-compliance${suffix}`, {
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to load checkpoint coverage");
  return response.json();
}

// ── Missed patrols (alert history) ───────────────────────────────────────────

export interface MissedPatrolAlert {
  id: string;
  hospital_id: string;
  location_id: string | null;
  alert_type: string;
  severity: string;
  message: string;
  minutes_overdue: number | null;
  last_scan_at: string | null;
  created_at: string;
  resolved: boolean;
  resolved_at: string | null;
  minutes_to_resolve: number | null;
  hospital_name: string | null;
  location_name: string | null;
  floor: string | null;
  building: string | null;
}

export interface MissedPatrolLocationRollup {
  location_id: string | null;
  location_name: string | null;
  hospital_name: string | null;
  alerts: number;
  open: number;
  worst_minutes_overdue: number | null;
  avg_minutes_to_resolve: number | null;
  latest_alert_at: string;
}

export interface MissedPatrolsResponse {
  date_range: ReportDateRange | null;
  advisory: string;
  /** Explains when observation began — a short history is not a clean record. */
  history_note: string | null;
  first_alert_at: string | null;
  summary: { alerts: number; open: number; resolved: number; truncated?: boolean };
  by_location: MissedPatrolLocationRollup[];
  alerts: MissedPatrolAlert[];
}

export async function fetchMissedPatrols(params: {
  hospitalId?: string;
  start?: string;
  end?: string;
  state?: "all" | "open" | "resolved";
} = {}): Promise<MissedPatrolsResponse> {
  const q = new URLSearchParams();
  if (params.hospitalId) q.set("hospital_id", params.hospitalId);
  if (params.start) q.set("start", params.start);
  if (params.end) q.set("end", params.end);
  if (params.state) q.set("state", params.state);
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const response = await fetch(`/api/reports/missed-patrols${suffix}`, {
    headers: authHeaders(),
  });
  await throwOnError(response, "Failed to load missed patrols");
  return response.json();
}
